import { formatInTimeZone } from 'date-fns-tz';
import { memo, useMemo } from 'react';

import { Button } from '@/ui';
import { cn } from '@/ui/lib/utils';

import type { CalendarEvent } from '../../types';
import {
  AGENDA_DAY_SPAN,
  addDaysInTimeZone,
  endOfDayInTimeZone,
  startOfDayInTimeZone
} from '../../utils/dateGrid';
import EventBlock from '../EventBlock';

import type { AgendaViewProps } from './types';

function defaultAgendaContent(event: CalendarEvent, timeZone: string) {
  const zone = event.timeZone ?? timeZone;
  const timeLabel = event.isAllDay ? 'All day' : formatInTimeZone(event.start, zone, 'h:mm a');
  return (
    <div className="flex items-center gap-2 truncate">
      <span className="w-16 shrink-0 text-xs tabular-nums opacity-90">{timeLabel}</span>
      <span className={cn('truncate font-medium', event.isCancelled && 'line-through')}>
        {event.title}
      </span>
    </div>
  );
}

function AgendaView({
  date,
  events,
  timeZone,
  isDateHighlighted,
  onEventClick,
  onSlotClick,
  renderEventContent,
  renderEventPopover
}: AgendaViewProps) {
  const days = useMemo(
    () => Array.from({ length: AGENDA_DAY_SPAN }, (_, i) => addDaysInTimeZone(date, i, timeZone)),
    [date, timeZone]
  );

  return (
    <div className="flex flex-col divide-y divide-uniform-gray-100 dark:divide-uniform-gray-800">
      {days.map((day) => {
        const dayStart = startOfDayInTimeZone(day, timeZone);
        const dayEnd = endOfDayInTimeZone(day, timeZone);
        const dayEvents = events
          .filter((event) => !event.isBackground && event.start < dayEnd && event.end > dayStart)
          .sort((a, b) => a.start.getTime() - b.start.getTime());
        const highlighted = isDateHighlighted?.(day);

        return (
          <div key={day.toISOString()} className="flex gap-4 py-2">
            <Button
              variant="ghost"
              color="neutral"
              onClick={() => onSlotClick?.({ start: dayStart, end: dayEnd, isAllDay: true })}
              className={cn(
                'h-auto w-24 shrink-0 justify-start px-0 text-left text-sm font-semibold',
                highlighted
                  ? 'text-green-700 dark:text-green-400'
                  : 'text-uniform-gray-700 dark:text-uniform-gray-200'
              )}>
              {formatInTimeZone(day, timeZone, 'EEE, MMM d')}
            </Button>
            <div className="flex flex-1 flex-col gap-1">
              {dayEvents.length === 0 ? (
                <span className="text-sm text-uniform-gray-400">No events</span>
              ) : (
                dayEvents.map((event) => (
                  <EventBlock
                    key={event.id}
                    event={event}
                    timeZone={timeZone}
                    compact
                    draggable={false}
                    onClick={onEventClick}
                    renderEventContent={
                      renderEventContent ?? ((e) => defaultAgendaContent(e, timeZone))
                    }
                    renderEventPopover={renderEventPopover}
                  />
                ))
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default memo(AgendaView);
